import Image from 'next/image';
import { useSnackbar } from 'notistack';
import { FormattedMessage, useIntl } from 'react-intl';

import { LanguageSlugEnum } from '@/types/regionTypes';
import { splitAtLastSpace } from '@/utils/string';

import { Typography } from '@/components/Typography';
import type { OrderAppCtaType } from '@/graphql/orderApp/api';
import type { CountryCode } from '@/graphql/saleor/api';
import {
  DESCRIPTION_ID,
  fetchAndSetCookies,
  isOricoFinancingAvailable,
  KAMEI_NO,
  KYAKU_SYU,
  ORICO_SIMULATION_URL,
} from '@/lib/payment/oricoFinancing';
import { getCurrentUrl } from '@/lib/util';
import { useRegionProvider } from '@/providers/RegionProvider';
import * as M from '@/views/PaymentView/components/OricoFinancing/messages';

import * as S from './OricoFinancingSentence.style';

import OricoFinancingIcon from '@/assets/icons/OricoFinancingIcon.svg';

interface OricoFinancingSentenceProps {
  amount?: number;
  country: CountryCode;
  variantCTA?: OrderAppCtaType;
}

export const OricoFinancingSentence = ({
  amount = 0,
  country,
  variantCTA,
}: OricoFinancingSentenceProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { locale } = useRegionProvider();
  const { formatMessage } = useIntl();

  if (!isOricoFinancingAvailable(country, variantCTA)) return null;

  const openOricoSimulation = async () => {
    // 1. Get the values for the form fields
    const backUrl = getCurrentUrl();
    const target = 'OricoSimulation'; //  Use a named target

    // 2. Define window dimensions and position.
    const width = 800; // Example width
    const height = 600; // Example height
    const left = (window.screen.width - width) / 2; // Center horizontally
    const top = (window.screen.height - height) / 2; // Center vertically

    // 3. Open the new window *before* submitting the form.
    const newWindow = window.open(
      '', // Use an empty string, important for form submission
      target, // Use the named target here
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,toolbar=no,menubar=no,location=no,status=no`,
    );

    // 4. Check if the window was successfully opened (popup blockers!).
    if (!newWindow) {
      enqueueSnackbar(
        'Please allow pop-ups for this website to open the Orico financing simulation window.',
        {
          variant: 'error',
        },
      );
      return; // Stop execution if the window didn't open.
    }
    newWindow.document.write('<p>Loading...</p>'); // Optional: Show loading message

    await fetchAndSetCookies();

    // 5. Create the form dynamically.
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = ORICO_SIMULATION_URL;
    form.target = target; //  Target the named window!
    form.style.display = 'none';

    // 6. Create hidden input fields (as before).
    const fields = {
      DESCRIPTION_ID,
      KAMEI_NO,
      KYAKU_SYU,
      BACK_URL: backUrl,
      TARGET: target, //still need to provide it, as it is part of the spec.
      RequestedLoanAmount: amount.toString(),
    };

    for (const [key, value] of Object.entries(fields)) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = value;
      form.appendChild(input);
    }

    // 7. Append and submit the form.
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  const isEn = locale.slug === LanguageSlugEnum.en;
  const installmentInfoText = formatMessage(M.defined.installmentInfo).trim();

  return (
    <S.FinancingContainer>
      {isEn ? (
        <S.FinancingTextWrapperEn>
          <S.FinancingText>
            {splitAtLastSpace(installmentInfoText)[0]}&nbsp;
          </S.FinancingText>
          <S.OricoWrapper>
            <S.FinancingText>
              {splitAtLastSpace(installmentInfoText)[1]}&nbsp;
            </S.FinancingText>
            <Image
              alt='orico_financing'
              height={12}
              src={OricoFinancingIcon}
              width={48}
            />
          </S.OricoWrapper>
        </S.FinancingTextWrapperEn>
      ) : (
        <S.FinancingTextWrapper>
          <S.FinancingText>
            <FormattedMessage {...M.defined.installmentInfo} /> &nbsp;
          </S.FinancingText>
          <Image
            alt='orico_financing'
            height={12}
            src={OricoFinancingIcon}
            width={48}
          />
        </S.FinancingTextWrapper>
      )}
      <Typography
        fontWeight='bold'
        onClick={openOricoSimulation}
        style={{ cursor: 'pointer' }}
        textDecoration='underline'
      >
        <FormattedMessage {...M.defined.installmentInfoCTA} />
      </Typography>
    </S.FinancingContainer>
  );
};
